import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { TenantProvider } from './context/TenantContext';
import { Home } from './pages/Home';
import { AboutPage as About } from './pages/public/About';
import { ContactPage as Contact } from './pages/public/Contact';
import { Faq } from './pages/public/Faq';
import { Contests } from './pages/public/Contests';
import { UpcomingContests } from './pages/public/UpcomingContests';
import { TeamPage as Team } from './pages/public/Team';
import { VoteRedirect } from './pages/VoteRedirect';
import { Login } from './pages/auth/Login';
import Dashboard from './pages/admin/Dashboard';
import { ContestList } from './pages/admin/contests/ContestList';
import { ContestantList } from './pages/admin/contestants/ContestantList';
import { VoteModal } from './components/vote/VoteModal';
import { ProtectedRoute } from './components/admin/ProtectedRoute';
import { Toaster } from 'sonner';
import { Profile } from './pages/admin/Profile';
import { PaymentCallback } from './pages/payment/PaymentCallback';
import { TransactionList } from './pages/admin/transactions/TransactionList';
import { ClientList } from './pages/admin/clients/ClientList';
import ClientFinances from './pages/admin/clients/ClientFinances';
import ClientRoutes from './routes/client.routes';
import { ScrollToTop } from './components/utils/ScrollToTop';
import { DynamicHTMLHead } from './components/utils/DynamicHTMLHead';
import { Classement } from './pages/public/Classement';
import { NotFound } from './pages/public/NotFound';
import { SitemapXML } from './pages/public/SitemapXML';
import { StripeCallback } from './pages/payment/StripeCallback';
import StripeSuccess from './pages/payment/StripeSuccess';
import StripeProcess from './pages/payment/StripeProcess';
import { AwardsPage } from './pages/awards/AwardsPage';
import { AwardDetailPage } from './pages/awards/AwardDetailPage';
import { AwardFormPage } from './pages/awards/AwardFormPage';
import { MessageList } from './pages/admin/messages/MessageList';
import { EmailManagement } from './pages/admin/emails/EmailManagement';
import { BlogPostList } from './pages/admin/blog/BlogPostList';
import { BlogPostEdit } from './pages/admin/blog/BlogPostEdit';
import { CategoryList } from './pages/admin/blog/CategoryList';
import { CommentList } from './pages/admin/blog/CommentList';
import { BlogHome } from './pages/blog/BlogHome';
import { BlogPostDetail } from './pages/blog/BlogPostDetail';
import { PortfolioHome } from './pages/portfolio/PortfolioHome';
import { ArtworkDetail } from './pages/portfolio/ArtworkDetail';
import { EditContestPage } from './pages/admin/contests/EditContestPage';
import PendingContestants from './pages/admin/contestants/PendingContestants';
import ContestRegistration from './pages/public/registration/ContestRegistration';
import ContestSubmission from './pages/public/submission/ContestSubmission';
import PendingContests from './pages/admin/contests/PendingContests';
import { AwardList } from './pages/admin/awards/AwardList';
import { PartnerList } from './pages/admin/partners/PartnerList';
import { PartnerEdit } from './pages/admin/partners/PartnerEdit';
import { ArtworkList } from './pages/admin/portfolio/ArtworkList';
import { ArtworkEdit } from './pages/admin/portfolio/ArtworkEdit';
import { CategoryList as PortfolioCategoryList } from './pages/admin/portfolio/CategoryList';
import { CrowdfundingList } from './pages/crowdfunding/CrowdfundingList';
import { CampaignDetail } from './pages/crowdfunding/CampaignDetail';
import { CampaignList } from './pages/admin/crowdfunding/CampaignList';
import { CampaignForm } from './pages/admin/crowdfunding/CampaignForm';
import { CampaignStats } from './pages/admin/crowdfunding/CampaignStats';
import EventsRoutes from './routes/events.routes';
import ShopRoutes from './routes/shop.routes';
import { InstructorApp } from './routes/instructorRoutes';
import MyTickets from './pages/public/tickets/MyTickets';
import TicketDetails from './pages/public/tickets/TicketDetails';

// Import des composants de la boutique
import ProductListPage from './pages/shop/ProductListPage';
import ProductDetailPage from './pages/shop/ProductDetailPage';
import ProductForm from './pages/admin/shop/ProductForm';
import CartPage from './pages/shop/CartPage';
import CheckoutPage from './pages/shop/CheckoutPage';
import AdminProductList from './pages/admin/shop/AdminProductList';
import AdminOrderList from './pages/admin/shop/AdminOrderList';
import AdminOrderDetails from './pages/admin/shop/AdminOrderDetails';
import ShopCategoryList from './pages/admin/shop/CategoryList';
import TenantConfigPage from './pages/admin/TenantConfig';
import MediaLibrary from './pages/admin/MediaLibrary';
import { CacheDebugPanel } from './components/debug/CacheDebugPanel';
import { CategoryManagement } from './pages/admin/CategoryManagement';
import { TemplateRoute } from './components/templates/TemplateRoute';

// Import des pages e-learning
import CourseListPage from './pages/elearning/CourseListPage';
import CourseDetailPage from './pages/elearning/CourseDetailPage';
import ElearningCartPage from './pages/elearning/ElearningCartPage';
import ElearningCheckoutPage from './pages/elearning/ElearningCheckoutPage';
import ElearningConfirmationPage from './pages/elearning/ElearningConfirmationPage';
import AdminCoursesPage from './pages/admin/elearning/AdminCoursesPage';
import CourseFormPage from './pages/admin/elearning/CourseFormPage';
import CourseBuilderPage from './pages/admin/elearning/CourseBuilderPage';
import AdminEnrollmentsPage from './pages/admin/elearning/AdminEnrollmentsPage';
import AdminCategoriesPage from './pages/admin/elearning/AdminCategoriesPage';
import AdminInstructorsPage from './pages/admin/elearning/AdminInstructorsPage';
import AdminStudentsPage from './pages/admin/elearning/AdminStudentsPage';
import AdminCourseReviewsPage from './pages/admin/elearning/AdminCourseReviewsPage';
import StudentDashboardPage from './pages/elearning/StudentDashboardPage';
import StudentCoursesPage from './pages/elearning/StudentCoursesPage';
import StudentProgressPage from './pages/elearning/StudentProgressPage';
import StudentCalendarPage from './pages/elearning/StudentCalendarPage';
import StudentAchievementsPage from './pages/elearning/StudentAchievementsPage';
import StudentQuizPage from './pages/elearning/StudentQuizPage';
import StudentGoalsPage from './pages/elearning/StudentGoalsPage';
import StudentOrdersPage from './pages/elearning/StudentOrdersPage';
import StudentNotificationsPage from './pages/elearning/StudentNotificationsPage';
import StudentMessagesPage from './pages/elearning/StudentMessagesPage';
import StudentProfilePage from './pages/elearning/StudentProfilePage';
import CoursePlayerPage from './pages/elearning/CoursePlayerPage';
import StudentAuthPage from './pages/elearning/StudentAuthPage';
import UnifiedAuthPage from './pages/auth/UnifiedAuthPage';

// Import routes Santé Psy Online
import { psyPublicRoutes, psyPatientRoutes, psyPractitionerRoutes, psyAdminRoutes } from './routes/psy.routes';

// Import pages admin Santé Psy
import AdminPractitionersList from './pages/admin/psy/AdminPractitionersList';
import AdminPatientsList from './pages/admin/psy/AdminPatientsList';
import AdminPractitionerVerification from './pages/admin/psy/AdminPractitionerVerification';
import AdminPsyAppointments from './pages/admin/psy/AdminPsyAppointments';
import AdminPsyTriage from './pages/admin/psy/AdminPsyTriage';

// Import pages admin Coupons
import CouponList from './pages/admin/coupons/CouponList';
import CouponForm from './pages/admin/coupons/CouponForm';
import CouponStats from './pages/admin/coupons/CouponStats';

// Import du routeur dynamique de services
import { DynamicServiceRouter } from './components/services/DynamicServiceRouter';

// Import routes Dating
import DatingRoutes from './routes/dating.routes';

// Import admin Dating pages
import DatingDashboard from './pages/admin/dating/DatingDashboard';
import DatingProfileList from './pages/admin/dating/DatingProfileList';
import DatingReportList from './pages/admin/dating/DatingReportList';
import DatingPlanList from './pages/admin/dating/DatingPlanList';
import DatingOrderList from './pages/admin/dating/DatingOrderList';

// Import admin Users/Permissions page
import AdminPermissions from './pages/admin/users/AdminPermissions';

const queryClient = new QueryClient();

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TenantProvider>
          <DynamicHTMLHead />
          <Router>
            <ScrollToTop />
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<TemplateRoute templateKey="home" defaultElement={<Home />} />} />
            <Route path="/auth" element={<UnifiedAuthPage />} />
            <Route path="/login" element={<UnifiedAuthPage />} />
            <Route path="/auth/login" element={<UnifiedAuthPage />} />
            <Route path="/about" element={<TemplateRoute templateKey="about" defaultElement={<About />} />} />
            <Route path="/contact" element={<TemplateRoute templateKey="contact" defaultElement={<Contact />} />} />
            <Route path="/sitemap.xml" element={<SitemapXML />} />
            <Route path="/sitemap" element={<SitemapXML />} />
            <Route path="/faq" element={<Faq />} />
            
            {/* Routes des services dynamiques */}
            <Route path="/services/*" element={<DynamicServiceRouter />} />
            
            <Route path="/contests" element={<Contests />} />
            <Route path="/upcoming-contests" element={<UpcomingContests />} />
            <Route path="/equipe" element={<Team />} />
            <Route path="/classement" element={<Classement />} />            
            {/* Santé Psy Online routes */}
            {psyPublicRoutes}
            {psyPatientRoutes}
            {psyPractitionerRoutes}
            
            {/* E-learning routes */}
            <Route path="/elearning" element={<CourseListPage />} />
            <Route path="/elearning/courses" element={<CourseListPage />} />
            <Route path="/elearning/courses/:slug" element={<CourseDetailPage />} />
            <Route path="/elearning/cart" element={<ElearningCartPage />} />
            <Route path="/elearning/checkout" element={<ElearningCheckoutPage />} />
            <Route path="/elearning/confirmation/:orderId" element={<ElearningConfirmationPage />} />
            <Route path="/elearning/auth" element={<UnifiedAuthPage />} />
            <Route path="/elearning/student/dashboard" element={<StudentDashboardPage />} />
            <Route path="/elearning/student/courses" element={<StudentCoursesPage />} />
            <Route path="/elearning/student/progress" element={<StudentProgressPage />} />
            <Route path="/elearning/student/calendar" element={<StudentCalendarPage />} />
            <Route path="/elearning/student/achievements" element={<StudentAchievementsPage />} />
            <Route path="/elearning/student/quiz" element={<StudentQuizPage />} />
            <Route path="/elearning/student/goals" element={<StudentGoalsPage />} />
            <Route path="/elearning/student/orders" element={<StudentOrdersPage />} />
            <Route path="/elearning/student/notifications" element={<StudentNotificationsPage />} />
            <Route path="/elearning/student/messages" element={<StudentMessagesPage />} />
            <Route path="/elearning/student/profile" element={<StudentProfilePage />} />

            <Route path="/elearning/player/:enrollmentId" element={<CoursePlayerPage />} />
            
            <Route path="/inscription/:contestCode" element={<ContestRegistration />} />
            <Route path="/soumettre-concours" element={<ContestSubmission />} />
            <Route path="/vote/:code" element={<VoteRedirect />} />
            
            {/* Crowdfunding routes */}
            <Route path="/crowdfunding" element={<CrowdfundingList />} />
            <Route path="/crowdfunding/:slug" element={<CampaignDetail />} />
            
            {/* Events routes */}
            <Route path="/events/*" element={<EventsRoutes />} />
            
            {/* Tickets routes */}
            <Route path="/tickets/:ticketCode" element={<TicketDetails />} />
            <Route path="/my-tickets" element={<MyTickets />} />
            
            {/* Payment routes */}
            <Route path="/payment/success" element={<PaymentCallback />} />
            <Route path="/payment/stripe/success" element={<StripeSuccess />} />
            <Route path="/payment/stripe/callback" element={<StripeCallback />} />
            <Route path="/payment/stripe/process" element={<StripeProcess />} />
            <Route path="/payment/cancel" element={<Navigate to="/contests" replace />} />
            <Route path="/payment/error" element={<Navigate to="/contests" replace />} />
            
            {/* Auth routes */}
            <Route path="/admin/login" element={<Login />} />
            <Route path="/client/login" element={<Login />} />

            {/* Protected admin routes */}
            <Route
              path="/admin/*"
              element={
                <ProtectedRoute>
                  <Routes>
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/contests" element={<ContestList />} />
                    <Route path="/contests/edit/:id" element={<EditContestPage />} />
                    <Route path="/contests/pending" element={<PendingContests />} />
                    <Route path="/contestants" element={<ContestantList />} />
                    <Route path="/contestants/pending" element={<PendingContestants />} />
                    <Route path="/transactions" element={<TransactionList />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/clients" element={<ClientList />} />
                    <Route path="/clients/:id/finances" element={<ClientFinances />} />
                    <Route path="/awards" element={<AwardList />} />
                    <Route path="/awards/new" element={<AwardFormPage />} />
                    <Route path="/awards/edit/:slug" element={<AwardFormPage />} />
                    <Route path="/crowdfunding" element={<CampaignList />} />
                    <Route path="/crowdfunding/create" element={<CampaignForm />} />
                    <Route path="/crowdfunding/edit/:slug" element={<CampaignForm />} />
                    <Route path="/crowdfunding/stats" element={<CampaignStats />} />
                    <Route path="/events" element={<Navigate to="/events/admin" replace />} />
                    <Route path="/messages" element={<MessageList />} />
                    <Route path="/emails" element={<EmailManagement />} />
                    <Route path="/blog" element={<BlogPostList />} />
                    <Route path="/blog/create" element={<BlogPostEdit />} />
                    <Route path="/blog/edit/:id" element={<BlogPostEdit />} />
                    <Route path="/blog/categories" element={<CategoryList />} />
                    <Route path="/blog/comments" element={<CommentList />} />
                    <Route path="/partners" element={<PartnerList />} />
                    <Route path="/partners/new" element={<PartnerEdit />} />
                    <Route path="/partners/edit/:id" element={<PartnerEdit />} />
                    <Route path="/portfolio" element={<ArtworkList />} />
                    <Route path="/portfolio/create" element={<ArtworkEdit />} />
                    <Route path="/portfolio/edit/:id" element={<ArtworkEdit />} />
                    <Route path="/portfolio/categories" element={<PortfolioCategoryList />} />
                    <Route path="/boutique" element={<AdminProductList />} />
                    <Route path="/boutique/creer" element={<ProductForm />} />
                    <Route path="/boutique/edit/:id" element={<ProductForm />} />
                    <Route path="/boutique/commandes" element={<AdminOrderList />} />
                    <Route path="/boutique/commande/:id" element={<AdminOrderDetails />} />
                    <Route path="/boutique/categories" element={<ShopCategoryList />} />
                    {/* E-learning admin routes */}
                    <Route path="/elearning" element={<AdminCoursesPage />} />
                    <Route path="/elearning/courses" element={<AdminCoursesPage />} />
                    <Route path="/elearning/create" element={<CourseFormPage />} />
                    <Route path="/elearning/courses/edit/:id" element={<CourseFormPage />} />
                    <Route path="/admin/elearning/courses/edit/:id" element={<CourseFormPage />} />
                    <Route path="/elearning/courses/builder/:id" element={<CourseBuilderPage />} />
                    <Route path="/elearning/enrollments" element={<AdminEnrollmentsPage />} />
                    <Route path="/elearning/categories" element={<AdminCategoriesPage />} />
                    <Route path="/elearning/instructors" element={<AdminInstructorsPage />} />
                    <Route path="/elearning/students" element={<AdminStudentsPage />} />
                    <Route path="/elearning/reviews" element={<AdminCourseReviewsPage />} />
                    {/* Santé Psy Online admin routes */}
                    <Route path="/psy/praticiens" element={<AdminPractitionersList />} />
                    <Route path="/psy/patients" element={<AdminPatientsList />} />
                    <Route path="/psy/praticiens/:id" element={<AdminPractitionerVerification />} />
                    <Route path="/psy/appointments" element={<AdminPsyAppointments />} />
                    <Route path="/psy/triage" element={<AdminPsyTriage />} />
                    {/* Coupons admin routes */}
                    <Route path="/coupons" element={<CouponList />} />
                    <Route path="/coupons/new" element={<CouponForm />} />
                    <Route path="/coupons/:id" element={<CouponForm />} />
                    <Route path="/coupons/:id/stats" element={<CouponStats />} />
                    {/* Dating admin routes */}
                    <Route path="/dating" element={<DatingDashboard />} />
                    <Route path="/dating/profiles" element={<DatingProfileList />} />
                    <Route path="/dating/reports" element={<DatingReportList />} />
                    <Route path="/dating/plans" element={<DatingPlanList />} />
                    <Route path="/dating/orders" element={<DatingOrderList />} />
                    {/* Users/Permissions admin routes */}
                    <Route path="/users/permissions" element={<AdminPermissions />} />
                    <Route path="/configuration" element={<TenantConfigPage />} />
                    <Route path="/mediatheque" element={<MediaLibrary />} />
                    <Route path="/tenants" element={<CategoryManagement />} />
                    <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
                  </Routes>
                </ProtectedRoute>
              }
            />

            {/* Protected client routes */}
            <Route
              path="/client/*"
              element={
                <ProtectedRoute>
                  <ClientRoutes />
                </ProtectedRoute>
              }
            />            

            {/* Awards routes */}
            <Route path="/awards" element={<AwardsPage />} />
            <Route path="/awards/:slug" element={<AwardDetailPage />} />
            <Route path="/blog" element={<BlogHome />} />
            <Route path="/blog/:slug" element={<BlogPostDetail />} />
            
            {/* Portfolio routes */}
            <Route path="/portfolio" element={<PortfolioHome />} />
            <Route path="/portfolio/:slug" element={<ArtworkDetail />} />
            
            {/* Boutique routes */}
            <Route path="/boutique/*" element={<ShopRoutes />} />
            
            {/* Instructor routes */}
            <Route path="/instructor/*" element={<InstructorApp />} />

            {/* Dating routes */}
            <Route path="/dating/*" element={<DatingRoutes />} />

            {/* Fallback route - Page 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          <VoteModal />
          <CacheDebugPanel />
          <Toaster richColors position="top-right" />
        </Router>
        </TenantProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;